import React, { useEffect, useState } from 'react';
import {
  Plus, Save, Trash2, Edit2, X, Users, Percent, Layers, Loader2, CheckCircle2, AlertCircle,
  Star, ArrowUp, ArrowDown,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const blankGroup = {
  name: '',
  description: '',
  discount_type: 'percent',
  percent_value: 10,
  sepr_rules: [{ from: 100, to: 200, discount: 30 }],
  is_default: false,
};

export default function CustomerGroupsEditor({ token }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null);  // id or 'new'
  const [draft, setDraft] = useState(blankGroup);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const r = await fetch(`${API_URL}/api/customer-groups/`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setGroups(await r.json());
    } catch (e) {
      setErr(e.message || 'Load failed');
    } finally { setLoading(false); }
  };

  // Load groups once on mount. The bare-rule disable is a hold-over from
  // before exhaustive-deps was made an error in CI; switching to the
  // explicit rule name keeps the lint configuration future-proof.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const startCreate = () => { setDraft({ ...blankGroup }); setEditing('new'); setSavedMsg(''); };
  const startEdit = (g) => {
    setDraft({
      ...blankGroup, ...g,
      sepr_rules: (g.sepr_rules || []).length ? g.sepr_rules : blankGroup.sepr_rules,
    });
    setEditing(g.id); setSavedMsg('');
  };
  const cancel = () => { setEditing(null); setDraft(blankGroup); setErr(''); };

  const save = async () => {
    setSaving(true); setErr(''); setSavedMsg('');
    try {
      const url = editing === 'new' ? `${API_URL}/api/customer-groups/` : `${API_URL}/api/customer-groups/${editing}`;
      const method = editing === 'new' ? 'POST' : 'PATCH';
      const r = await fetch(url, { method, headers, body: JSON.stringify(draft) });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || `HTTP ${r.status}`);
      setSavedMsg(editing === 'new' ? 'Group created' : 'Group updated');
      await load();
      setEditing(null);
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"?\n\n${g.members_count || 0} customers are currently in this group — they will be unassigned (no discount).`)) return;
    try {
      const r = await fetch(`${API_URL}/api/customer-groups/${g.id}`, { method: 'DELETE', headers });
      if (!r.ok) { const b = await r.json(); throw new Error(b.detail || `HTTP ${r.status}`); }
      await load();
    } catch (e) { alert('Delete failed: ' + e.message); }
  };

  const addSeprRow = () => setDraft(d => ({ ...d, sepr_rules: [...d.sepr_rules, { from: 0, to: 0, discount: 0 }] }));
  const removeSeprRow = (idx) => setDraft(d => ({ ...d, sepr_rules: d.sepr_rules.filter((_, i) => i !== idx) }));
  const setSepr = (idx, key, val) => setDraft(d => ({
    ...d,
    sepr_rules: d.sepr_rules.map((r, i) => i === idx ? { ...r, [key]: Number(val) || 0 } : r),
  }));

  if (loading) return <div className="p-8 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  return (
    <div className="space-y-4" data-testid="customer-groups-editor">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Users className="w-5 h-5 text-emerald-600" /> Customer groups (discount tiers)</h3>
          <p className="text-xs text-slate-500 mt-0.5">Assign a group to a customer from their profile. Discount is applied automatically on every new order AFTER the price coefficient.</p>
        </div>
        {!editing && (
          <button onClick={startCreate} data-testid="cg-create" className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:shadow-md text-white rounded-lg text-sm font-semibold flex items-center gap-2">
            <Plus className="w-4 h-4" /> New group
          </button>
        )}
      </div>

      {savedMsg && <div className="px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-sm flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> {savedMsg}</div>}
      {err && <div className="px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> {err}</div>}

      {/* Editor form */}
      {editing && (
        <div className="bg-white rounded-xl border-2 border-emerald-200 p-5 shadow-sm" data-testid="cg-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Name *" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="VIP 15% off" testid="cg-name" />
            <Input label="Description" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} placeholder="Shown under the name in the dropdown" />
          </div>

          <div className="mt-4">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Discount type *</p>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setDraft({ ...draft, discount_type: 'percent' })}
                data-testid="cg-type-percent"
                className={`px-4 py-2.5 border rounded-lg text-sm font-semibold flex items-center justify-center gap-2 ${draft.discount_type === 'percent' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-700 border-slate-200 hover:bg-emerald-50'}`}>
                <Percent className="w-4 h-4" /> Flat percentage
              </button>
              <button type="button" onClick={() => setDraft({ ...draft, discount_type: 'sepr' })}
                data-testid="cg-type-sepr"
                className={`px-4 py-2.5 border rounded-lg text-sm font-semibold flex items-center justify-center gap-2 ${draft.discount_type === 'sepr' ? 'bg-purple-500 text-white border-purple-500' : 'bg-white text-slate-700 border-slate-200 hover:bg-purple-50'}`}>
                <Layers className="w-4 h-4" /> SEPR (staircase)
              </button>
            </div>
          </div>

          {draft.discount_type === 'percent' ? (
            <div className="mt-4 p-4 bg-emerald-50/60 border border-emerald-200 rounded-lg">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Percentage off (%)</label>
              <input type="number" step="0.5" min="0" max="100" value={draft.percent_value}
                onChange={(e) => setDraft({ ...draft, percent_value: Number(e.target.value) })}
                data-testid="cg-percent-value"
                className="mt-1 w-full md:w-40 px-3 py-2 border border-emerald-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              <p className="text-[11px] text-slate-500 mt-1.5 italic">Example: <strong>10</strong> → every order is 10% cheaper (rounded to nearest €5).</p>
            </div>
          ) : (
            <div className="mt-4 p-4 bg-purple-50/60 border border-purple-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">SEPR ranges (flat EUR off by order total)</label>
                <button type="button" onClick={addSeprRow} data-testid="cg-sepr-add-row"
                  className="px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white text-xs rounded-lg font-semibold flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Range
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mb-2 italic">If the order total falls into a range, that flat discount (in €) is applied. Set <strong>to = 0</strong> for "no upper limit".</p>
              <div className="space-y-2">
                {(draft.sepr_rules || []).map((r, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`cg-sepr-row-${i}`}>
                    <span className="col-span-1 text-xs font-bold text-purple-700 text-center">#{i + 1}</span>
                    <div className="col-span-3">
                      <label className="text-[10px] text-slate-500 font-bold uppercase">From €</label>
                      <input type="number" min="0" value={r.from} onChange={(e) => setSepr(i, 'from', e.target.value)}
                        data-testid={`cg-sepr-from-${i}`}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
                    </div>
                    <div className="col-span-3">
                      <label className="text-[10px] text-slate-500 font-bold uppercase">To €</label>
                      <input type="number" min="0" value={r.to} onChange={(e) => setSepr(i, 'to', e.target.value)}
                        data-testid={`cg-sepr-to-${i}`}
                        placeholder="0 = ∞"
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
                    </div>
                    <div className="col-span-4">
                      <label className="text-[10px] text-slate-500 font-bold uppercase">Discount €</label>
                      <input type="number" min="0" step="0.5" value={r.discount} onChange={(e) => setSepr(i, 'discount', e.target.value)}
                        data-testid={`cg-sepr-discount-${i}`}
                        className="w-full px-2 py-1.5 border border-purple-300 rounded text-sm font-bold text-purple-700" />
                    </div>
                    <div className="col-span-1 flex justify-end pt-4">
                      <button type="button" onClick={() => removeSeprRow(i)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Remove range">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <label className="mt-4 flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={!!draft.is_default}
              onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })}
              className="mt-0.5 w-4 h-4 text-emerald-600 rounded" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-amber-500" /> Default group</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Shown first in the profile dropdown.</p>
            </div>
          </label>

          <div className="mt-5 flex justify-end gap-2">
            <button onClick={cancel} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-semibold flex items-center gap-1.5">
              <X className="w-4 h-4" /> Cancel
            </button>
            <button onClick={save} disabled={saving || !draft.name.trim()}
              data-testid="cg-save"
              className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:shadow-md text-white rounded-lg text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save group
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {!editing && (
        groups.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-10 text-center">
            <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No customer groups yet. Click "New group" to create one.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {groups.map((g) => (
              <div key={g.id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all" data-testid={`cg-card-${g.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-base font-bold text-slate-800 truncate">{g.name}</h4>
                      {g.is_default && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold flex items-center gap-0.5"><Star className="w-3 h-3" />DEFAULT</span>}
                    </div>
                    {g.description && <p className="text-xs text-slate-500 mt-0.5">{g.description}</p>}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => startEdit(g)} data-testid={`cg-edit-${g.id}`} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Edit">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(g)} data-testid={`cg-delete-${g.id}`} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100">
                  {g.discount_type === 'percent' ? (
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold flex items-center gap-1"><Percent className="w-3 h-3" /> {g.percent_value}% OFF</span>
                      <span className="text-xs text-slate-500">applied to every order</span>
                    </div>
                  ) : (
                    <div>
                      {/* Defensive: legacy groups (and the API default
                          for percent-type groups) can omit sepr_rules
                          entirely — fall back to [] so the chip and
                          list don't crash on undefined.length / .map. */}
                      {(() => {
                        const rules = Array.isArray(g.sepr_rules) ? g.sepr_rules : [];
                        return (
                          <>
                            <span className="px-2.5 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-bold flex items-center gap-1 w-fit"><Layers className="w-3 h-3" /> SEPR · {rules.length} range{rules.length === 1 ? '' : 's'}</span>
                            <ul className="mt-2 text-[11px] text-slate-600 space-y-0.5">
                              {rules.map((r, i) => (
                                <li key={i} className="font-mono">€{r.from}–{r.to > 0 ? `€${r.to}` : '∞'} → <strong className="text-purple-700">−€{r.discount}</strong></li>
                              ))}
                            </ul>
                          </>
                        );
                      })()}
                    </div>
                  )}
                  <p className="mt-2 text-[10px] text-slate-400">{g.members_count || 0} customers in this group</p>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

const Input = ({ label, value, onChange, placeholder = '', testid }) => (
  <div>
    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">{label}</label>
    <input
      type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      data-testid={testid}
      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
    />
  </div>
);
